import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Page, PageHeader, fmtEur } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Eye, Columns3, FileStack, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import BrochureRigheEditor from "@/components/BrochureRigheEditor";

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

async function fetchPdf(params, path = "/packages/schede-pdf") {
  const token = localStorage.getItem("ristruttura_token") || "";
  const qs = new URLSearchParams(params).toString();
  const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api${path}?${qs}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!resp.ok) { let m = `HTTP ${resp.status}`; try { m = (await resp.json()).detail || m; } catch (_) {} throw new Error(m); }
  return URL.createObjectURL(await resp.blob());
}

function PkgCard({ p, onPreview, onDownload }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-4 flex flex-col" style={{ borderTop: `5px solid ${p.color || "#0d9488"}` }} data-testid={`scheda-card-${p.id}`}>
      <div className="text-lg font-bold" style={{ color: p.color }}>{p.name}</div>
      <div className="text-xs text-zinc-500 line-clamp-2 min-h-[2rem]">{p.subtitle}</div>
      <div className="mt-2 text-2xl font-extrabold">{fmtEur(p.price_per_m2)}<span className="text-xs font-normal text-zinc-500"> /m² · IVA esclusa</span></div>
      <div className="text-xs text-zinc-500 mt-1">{(p.items || []).length} voci incluse{(p.listini_items || []).length ? ` · ${p.listini_items.length} finiture` : ""}</div>
      {(p.items || []).length === 0 && <div className="text-[11px] text-amber-700 mt-1">Nessuna voce configurata</div>}
      <div className="flex gap-2 mt-auto pt-3">
        <Button size="sm" variant="outline" onClick={onPreview} data-testid={`scheda-preview-${p.id}`}><Eye className="h-4 w-4 mr-1" />Anteprima</Button>
        <Button size="sm" onClick={onDownload} data-testid={`scheda-download-${p.id}`} style={{ background: "var(--brand)", color: "white" }}><Download className="h-4 w-4 mr-1" />PDF</Button>
      </div>
    </div>
  );
}

export default function SchedePacchetti() {
  const [pkgs, setPkgs] = useState([]);
  const { user } = useAuth();
  const isAdmin = (user?.role || "").toLowerCase() === "admin";
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    api.get("/packages").then((r) => setPkgs((r.data || []).sort((a, b) => (a.price_per_m2 || 0) - (b.price_per_m2 || 0))));
  }, []);

  const params = (extra) => ({ ...extra });

  const show = async (extra) => {
    setPreview({ loading: true, pages: [] });
    try {
      const { data } = extra.bagno
        ? await api.get("/brochure/bagno", { params: { confronto: !!extra.confronto, tier_id: extra.tier_id || "", formato: "preview" } })
        : await api.get("/packages/schede-preview", { params: params(extra) });
      setPreview({ loading: false, pages: data.pages || [], extra });
    } catch (e) { setPreview(null); toast.error(`Errore anteprima: ${e.response?.data?.detail || e.message}`); }
  };

  const download = async (extra, filename) => {
    const id = toast.loading("Generazione PDF…");
    try {
      const url = extra.bagno
        ? await fetchPdf({ confronto: !!extra.confronto, tier_id: extra.tier_id || "" }, "/brochure/bagno")
        : await fetchPdf(params(extra));
      if (isIOS()) { if (!window.open(url, "_blank")) { const a = document.createElement("a"); a.href = url; a.download = filename; a.click(); } }
      else { const a = document.createElement("a"); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove(); }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.dismiss(id); toast.success(isIOS() ? "PDF aperto: Condividi → Salva su File o Stampa" : "PDF scaricato");
    } catch (e) { toast.dismiss(id); toast.error(`Errore PDF: ${e.message}`); }
  };

  return (
    <div data-testid="schede-pacchetti-page">
      <PageHeader title="Schede Pacchetti" subtitle="Brochure da consegnare ai clienti: voci incluse (quantità solo dove fisse), solo prezzo al m² IVA esclusa. Sempre aggiornate con le modifiche ai pacchetti." />
      <Page>
        <div className="flex flex-wrap items-end gap-3 mb-5">
          <Button variant="outline" onClick={() => show({ confronto: true })} data-testid="schede-confronto-preview"><Eye className="h-4 w-4 mr-1" />Anteprima confronto</Button>
          <Button onClick={() => download({ confronto: true }, "Confronto_pacchetti.pdf")} data-testid="schede-confronto-download" style={{ background: "var(--brand)", color: "white" }}><Columns3 className="h-4 w-4 mr-1" />Scarica confronto 4 pacchetti</Button>
          <Button variant="outline" onClick={() => download({}, "Schede_pacchetti.pdf")} data-testid="schede-tutte-download"><FileStack className="h-4 w-4 mr-1" />Scarica tutte le schede</Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {pkgs.map((p) => (
            <PkgCard key={p.id} p={p} onPreview={() => show({ package_id: p.id })} onDownload={() => download({ package_id: p.id }, `Pacchetto_${p.name}.pdf`)} />
          ))}
        </div>
        <h2 className="text-lg font-bold mt-10 mb-1">Ristrutturazione bagno</h2>
        <p className="text-sm text-zinc-500 mb-3">Brochure dei livelli Silver, Gold e Platinum (lavori inclusi + sanitari), prezzo a bagno IVA esclusa.</p>
        <div className="flex flex-wrap gap-2 mb-2">
          <Button variant="outline" onClick={() => show({ bagno: true, confronto: true })} data-testid="bagno-confronto-preview"><Eye className="h-4 w-4 mr-1" />Anteprima confronto bagno</Button>
          <Button onClick={() => download({ bagno: true, confronto: true }, "Confronto_bagno.pdf")} data-testid="bagno-confronto-download" style={{ background: "var(--brand)", color: "white" }}><Columns3 className="h-4 w-4 mr-1" />Scarica confronto bagno</Button>
          {["silver", "gold", "platinum"].map((t) => (
            <Button key={t} variant="outline" onClick={() => show({ bagno: true, tier_id: `bagno-${t}` })} data-testid={`bagno-preview-${t}`}><Eye className="h-4 w-4 mr-1" />Bagno {t[0].toUpperCase() + t.slice(1)}</Button>
          ))}
        </div>
        {isAdmin && (<>
          <h2 className="text-lg font-bold mt-10 mb-1">Modifica il confronto pacchetti</h2>
          <p className="text-sm text-zinc-500 mb-3">Scrivi tu la tabella del confronto: righe, titoli e contenuto di ogni casella. In fondo trovi il promemoria delle voci incluse in ogni pacchetto. I prezzi restano sempre quelli della Gestione Pacchetti.</p>
          <BrochureRigheEditor />
        </>)}
        <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
          <DialogContent className="max-w-5xl w-[96vw] max-h-[92vh] overflow-y-auto p-3 sm:p-5" data-testid="schede-preview">
            <DialogHeader><DialogTitle>Anteprima</DialogTitle></DialogHeader>
            {preview?.loading ? (
              <div className="flex items-center justify-center gap-2 py-20 text-zinc-500" data-testid="schede-preview-loading"><Loader2 className="h-5 w-5 animate-spin" />Preparazione anteprima…</div>
            ) : (
              <div className="space-y-3 bg-zinc-100 p-2 rounded">
                {(preview?.pages || []).map((src, i) => <img key={i} src={src} alt={`Pagina ${i + 1}`} className="w-full shadow bg-white" data-testid={`schede-preview-page-${i}`} />)}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setPreview(null)} data-testid="schede-preview-close">Chiudi</Button>
              {preview && !preview.loading && <Button onClick={() => download(preview.extra, preview.extra?.bagno ? (preview.extra?.confronto ? "Confronto_bagno.pdf" : `Bagno_${preview.extra?.tier_id || ""}.pdf`) : preview.extra?.confronto ? "Confronto_pacchetti.pdf" : `Pacchetto_${pkgs.find((x) => x.id === preview.extra?.package_id)?.name || ""}.pdf`)} data-testid="schede-preview-download" style={{ background: "var(--brand)", color: "white" }}><Download className="h-4 w-4 mr-1" />Scarica PDF</Button>}
            </div>
          </DialogContent>
        </Dialog>
      </Page>
    </div>
  );
}
